import { Key } from "@tylertech/tyler-api-types";
import { EMPTY_GUID, GuidHelper, StringHelper } from "./helpers";

class Utilities
{

    static IsNullOrUndefined(value: any): boolean
    {
        return value === undefined || value === null;
    }

    static IsNullOrUndefinedOrEmpty(value: { length: number; }): boolean
    {
        return Utilities.IsNullOrUndefined(value) || value.length === 0;
    }

    static IsNullOrUndefinedOrEmptyObject(value: unknown): boolean
    {
        return Utilities.IsNullOrUndefined(value) || Object.keys(value).length === 0;
    }

    static IsNullOrUndefinedOrZero(value: unknown): boolean
    {
        return Utilities.IsNullOrUndefined(value) || value === 0;
    }

    /**
     * @deprecated Use `StringHelper.stringify(model)`
     */
    static stringify(model: unknown): string
    {
        return JSON.stringify(model, this.jsonNullRemover);
    }

    /**
     * Returns null if a is Nan, otherwise a.
     */
    static nullIfNaN(a: number): number
    {
        return isNaN(a) ? null : a;
    }

    static toLower(value: string): string
    {
        if (!value)
        {
            return "";
        }
        return value.toLowerCase();
    }

    /**
     * Returns null if a equals b, otherwise a.
     */
    static nullIf(a: number, b: number): number
    {
        return a === b ? null : a;
    }

    /**
     * Returns null if value is undefined, null, or value.ToString() is undefined or null, otherwise value.
     */
    static nullIfEmpty<T>(value: T): T
    {
        if (Utilities.IsNullOrUndefined(value) || Utilities.IsNullOrUndefinedOrEmpty(value.toString()))
        {
            return null;
        }
        return value;
    }

    static groupBy<T1, T2>(list, keyGetter): Map<T1, T2>
    {
        const map = new Map();
        list.forEach((item) =>
        {
            const key = keyGetter(item);
            const collection = map.get(key);
            if (!collection)
            {
                map.set(key, [item]);
            } else
            {
                collection.push(item);
            }
        });
        return map;
    }

    /** @deprecated - use EMPTY_GUID */
    static readonly emptyGuid = EMPTY_GUID;

    /** @deprecated - use GuidHelper */
    static isGuid(value: unknown): value is string
    {
        return GuidHelper.isGuid(value);
    }

    /** @deprecated - use GuidHelper */
    static IsGoodGuid(guid: string): boolean
    {
        return GuidHelper.IsGoodGuid(guid);
    }

    /** @deprecated - OData things belong in library/odata... */
    static escapeODataString(value: string): string
    {
        return encodeURIComponent(value
            .replace(/'/g, "''")
            .replace(/#/g, "%23")
            .replace(/\+/g, "%2B"));
    }

    /** @deprecated - use GuidHelper */
    static generateRandomGuid(): string
    {
        return GuidHelper.newGuid();
    }

    static generateRandomHTMLId(): string
    {
        return `a${this.generateRandomGuid().replace(/-/g, "")}`;
    }

    static parseSearchText(searchText: string, delimiter: string = " - ", index: number = 1): string
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(searchText) || searchText.indexOf(delimiter) < 0)
        {
            return searchText;
        }
        const split = searchText.split(delimiter);
        return split.length > index ? split[index] : split[0];
    }

    static isMobile(): boolean
    {
        let isMobile = false;
        if (
            /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
                navigator.userAgent
            ) ||
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
                navigator.userAgent.substr(0, 4)
            )
        )
        {
            isMobile = true;
        }
        return isMobile;
    }

    static toShortDateString(value: Date): string
    {
        if (Utilities.IsNullOrUndefined(value))
        {
            return null;
        }
        value = new Date(value.toString());
        if (!value || isNaN(value.getTime()))
        {
            return null;
        }
        const year = value.getFullYear();
        const month = value.getMonth() + 1;
        const day = value.getDate();
        return month + "/" + day + "/" + year;
    }

    /** Used by library/utilitybilling */
    static stripTimezone(value: Date): Date
    {
        const userTimezoneOffset = value.getTimezoneOffset() * 60000;
        const newDate = new Date(value);
        newDate.setTime(value.getTime() + userTimezoneOffset);
        return newDate;
    }

    /** Used by library/utilitybilling */
    static addTimezone(value: Date): Date
    {
        const userTimezoneOffset = value.getTimezoneOffset() * 60000;
        const newDate = new Date(value);
        newDate.setTime(value.getTime() - userTimezoneOffset);
        return newDate;
    }

    /** @deprecated - use GuidHelper */
    static isEmptyGuid(value: string): boolean
    {
        return GuidHelper.isEmptyGuid(value);
    }

    /**
     * @deprecated Use the type-guards from library/core instead of this. (`isHttpResponseError`, `isExecutionResult`, `isErroredExecutionResult`)
     */
    static getMessage(e, defaultMessage: string): string
    {
        return Utilities.IsNullOrUndefined(e) ||
            Utilities.IsNullOrUndefinedOrEmpty(e.error.Message)
            ? defaultMessage
            : e.error.Message;
    }

    /** @deprecated This should live in library/metadata */
    static getVerificationMesssage(error: any): string
    {
        if (Utilities.IsNullOrUndefined(error)) return "";
        if (!Utilities.IsNullOrUndefined(error.data) && !Utilities.IsNullOrUndefined(error.data.AdditionalInfo))
        {
            const verificationInfo = JSON.parse(error.data.AdditionalInfo);
            return this.getVerificationMesssage(verificationInfo);
        }
        if (Utilities.IsNullOrUndefined(error.Verifications))
        {
            if (Utilities.IsNullOrUndefined(error.FieldErrors))
            {
                return "";
            }
            return Object.values(error.FieldErrors)
                .join("\n");
        }
        else
        {
            const errorString: string[] = [];
            error.Verifications.forEach(v =>
            {
                Object.keys(v.FieldErrors)
                    .forEach(p =>
                    {
                        v.FieldErrors[p].forEach(e =>
                        {
                            errorString.push(e);
                        });
                    });
            });
            return errorString.join("<br>");
        }
    }

    /** @deprecated Use NavigationService from library/core */
    static goToApp(appPath: string): void
    {
        const tenant = window.location.pathname.split("/")[1].replace(/[^a-zA-Z0-9_-]/g, "");
        window.location.href = "/" + tenant + "/apps/" + appPath;
    }

    static arraysEqual(arr1, arr2): boolean
    {
        if (this.IsNullOrUndefined(arr1)) return false;
        if (this.IsNullOrUndefined(arr2)) return false;
        if (arr1.length !== arr2.length) return false;
        for (let i = arr1.length; i--;)
        {
            if (this.isObject(arr1[i]) && this.isObject(arr2[i]))
            {
                if (!this.objectsEqual(arr1[i], arr2[i]))
                {
                    return false;
                }
            }
            else if (arr1[i] !== arr2[i]) return false;
        }
        return true;
    }

    static objectsEqual(a, b): boolean
    {
        if (this.IsNullOrUndefined(a)) return false;
        if (this.IsNullOrUndefined(b)) return false;
        if (a === b) return true;
        const aProps = Object.getOwnPropertyNames(a);
        const bProps = Object.getOwnPropertyNames(b);
        if (aProps.length !== bProps.length)
        {
            return false;
        }
        if (Array.isArray(a))
        {
            if (!this.arraysEqual(a, b))
            {
                return false;
            }
        }
        for (let i = 0; i < aProps.length; i++)
        {
            const propName = aProps[i];
            if (Array.isArray(a[propName]) && !propName.startsWith("$$Parent"))
            {
                if (!this.arraysEqual(a[propName], b[propName]))
                {
                    return false;
                }
            }
            else if (
                this.isObject(a[propName]) &&
                !propName.startsWith("$$Parent") &&
                !propName.startsWith("$FormData")
            )
            {
                if (!this.objectsEqual(a[propName], b[propName]))
                {
                    return false;
                }
            }
            else if (a[propName] !== b[propName])
            {
                return false;
            }
        }
        return true;
    }

    /*
        private static isFormattedStringNumeric(value: any): boolean
        {
            const num = +value.toString()
                .replace("$", "")
                .replace(/,/g, "")
                .replace("(", "")
                .replace(")", "");
            if (!isNaN(num) && typeof num === "number")
            {
                return true;
            }
            return false;
        }

        private static getNumberFromFormattedString(value: any): any
        {
            if (this.isFormattedStringNumeric(value) && value !== "")
            {
                return +value.toString()
                    .replace(/[$,()]/g, "");
            }
            return value;
        }
    */

    /**
     * @deprecated
     */
    private static jsonNullRemover(key: any, value: any): any
    {
        if (key.startsWith("$")) return undefined;
        if (value === null) return undefined;
        return value;
    }

    static roundCents(val): number
    {
        return Math.round(val * 100) / 100;
    }

    static deepCopy<T = unknown>(source: T): T
    {
        // Make sure null and undefined are returned as such
        if (this.IsNullOrUndefined(source))
        {
            return source;
        }

        if (typeof source === "function")
        {
            throw new Error("Cannot copy function references");
        }

        if (source instanceof Date)
        {
            return new Date(source) as unknown as T;
        }

        if (Array.isArray(source))
        {
            return source.map(x => this.deepCopy(x)) as unknown as T;
        }

        if (typeof source === "object")
        {
            const clone: Record<string, unknown> = {};
            for (const field in source)
            {
                const value = source[field];
                if (field.startsWith("$"))
                {
                    clone[field] = value;
                }
                else
                {
                    clone[field] = this.deepCopy(value);
                }
            }
            return clone as unknown as T;
        }

        // Everything else should be a simple primitive.
        return source;
    }

    /**
     * @deprecated use `StringHelper.parse<T>`
     */
    static parseJson(str: string): any
    {
        return StringHelper.parse(str);
    }

    static mergeEntityCollections(
        oldCollection: Record<string, any[]>,
        newCollection: Record<string, any[]>,
        setNulls: boolean = true
    ): any
    {
        return this.merge(oldCollection, newCollection, setNulls, false, true);
    }

    static mergeArray(
        oldValue: any[],
        newValue: any[],
        parent: any,
        setNulls: boolean = true,
        isEntityCollection: boolean = false
    ): void
    {
        if (this.arraysEqual(oldValue, newValue)) return;
        if (Utilities.IsNullOrUndefined(newValue)) newValue = [];
        for (let index = 0; index < newValue.length; index++)
        {
            const element = newValue[index];
            //entity collection (OtherEntities) dont have a parent in a way that is relevant
            if (!isEntityCollection && parent && !this.IsNullOrUndefined(element) && this.isObject(element))
            {
                const parentValue = parent.$isEntityWrapper ? parent.$value : parent;
                if (!element.$$Parent) element.$$Parent = parentValue;
            }
            // merge each element
            if (oldValue.length > index)
            {
                if (this.isObject(oldValue[index]) && this.isObject(element))
                {
                    oldValue[index] = this.merge(oldValue[index], element, setNulls);
                }
                else
                {
                    oldValue[index] = element;
                }
            }
            else
            {
                // if new list is larger then create a new one copying over meta properties
                if (oldValue.length > 0)
                {
                    const oldElement = oldValue[0];
                    for (const oldField in oldElement)
                    {
                        if (oldField.startsWith("$") && !oldField.startsWith("$recalculating") && oldField !== "$$model" && oldField !== "$$AddedRowId")
                            element[oldField] = oldElement[oldField];
                    }
                }
                oldValue.push(element);
            }
        }
        // if the old list is larger truncate the list
        if (oldValue.length > newValue.length) oldValue.length = newValue.length;
    }

    /**
     * Deeply merges the values of the source object into the destination object.
     * @param destination
     * @param source
     * @param setNulls fields that do not exist in source set to null/empty in result
     * @returns
     */
    static merge(
        destination: any,
        source: any,
        setNulls: boolean = true,
        isParent: boolean = false,
        isEntityCollection: boolean = false
    ): any
    {
        const result = destination;
        if (!source)
        {
            if (destination.$isEntityWrapper)
            {
                destination.replaceTarget({}, true);
            }
            else
            {
                this.setNulls(destination, {});
            }
            return destination;
        }
        //if new source object is missing $ properties, get them from the destination object
        for (const field in result)
        {
            if (field.startsWith("$") && !field.startsWith("$recalculating") && source[field] === undefined)
                source[field] = result[field];
        }
        for (const field in source)
        {
            if (field.startsWith("$") && field !== "$$Parent") continue;
            const oldValue = field in result ? result[field] : undefined;
            const newValue = source[field];
            if (Array.isArray(oldValue) && Array.isArray(newValue))
            {
                if (!isParent) this.mergeArray(oldValue, newValue, destination, setNulls, isEntityCollection);
            }
            else if (field !== "$$Parent" && this.isObject(oldValue) && this.isObject(newValue))
            {
                if (result["$ignoreModify"])
                    oldValue["$ignoreModify"] = true;
                result[field] = this.merge(oldValue, newValue, setNulls);
                delete oldValue["$ignoreModify"];
            }
            else
            {
                result[field] = newValue;
            }
        }
        if (!!source["SerializedRowState"])
            result["SerializedRowState"] = source["SerializedRowState"];
        if (setNulls)
        {
            this.setNulls(result, source);
            if (result.$isEntityWrapper)
            {
                if (source.$$TypeName && result.$$TypeName !== source.$$TypeName)
                {
                    result.$$TypeName = source.$$TypeName;
                }
            }
        }
        return result;
    }

    public static isObject<T>(toTest: T): toTest is any
    {
        return typeof toTest === "object" && !this.IsNullOrUndefined(toTest);
    }

    static setNulls(destination: any, source: any): any
    {
        if (destination === source) return destination;
        const result = destination;
        for (const field in destination)
        {
            if (!field.startsWith("$") && !(field in source))
            {
                if (Array.isArray(destination[field]))
                {
                    result[field].length = 0;
                }
                else if (!!destination[field] && destination[field].$isEntityWrapper)
                {
                    result[field].replaceTarget({}, true);
                }
                else
                {
                    result[field] = null;
                }
            }
        }
        return result;
    }

    /**
     * Find the Key that corresponds to the first letter of a label (e.g. 'Yes' = 'Y')
     * @param label
     * @returns Key code
     */
    public static getShortcutKeyForLabel(label: string): Key | null
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(label))
            return null;
        const firstChar = label.charAt(0).toUpperCase();
        if (firstChar < 'A' || firstChar > 'Z')
            return null;
        const keyIndex = firstChar.charCodeAt(0) - 'A'.charCodeAt(0);
        return keyIndex in Key ? keyIndex as Key : null;
    }

    static toSentenceCase(s: string): string
    {
        if (!s)
        {
            return "";
        }
        return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    }
}
export { Utilities };
